import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsDatePickerComponent,
  YdsSpinnerComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  firstDayOfMonthFilter,
  formatDisplayDate,
  isInvalid,
  lastDayOfMonthFilter,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { GeneralLedger, GeneralledgerService } from '../generalledger.service';
import { deleteGeneralLedgerWithChecks } from '../generalledger-shared';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';

const MAX_END_DATE_ISO = '9999-12-31';

@Component({
  selector: 'app-generalledger-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './generalledger-editable.component.html',
  styleUrl: './generalledger-editable.component.scss',
})
export class GeneralledgerEditableComponent {
  readonly isInvalid = isInvalid;
  readonly firstDayOfMonthFilter = firstDayOfMonthFilter;
  readonly lastDayOfMonthFilter = lastDayOfMonthFilter;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private generalLedgerService = inject(GeneralledgerService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Id of the general ledger being edited, or null in add mode. */
  readonly generalLedgerId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<GeneralLedger | null>(null);

  readonly detailsEditing = signal(false);

  readonly form: FormGroup = this.formBuilder.group(
    {
      generalLedgerName: ['', Validators.required],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator('startDate', 'endDate') },
  );

  /** Error message shown when the start date is not before the end date. */
  endDateError(): string {
    return this.form.hasError('startBeforeEnd') &&
      (this.form.get('startDate')?.touched || this.form.get('endDate')?.touched)
      ? 'Begindatum moet voor einddatum liggen'
      : '';
  }

  /** Whether we are creating a new general ledger (no id in the route). */
  readonly isAddMode = computed(() => this.generalLedgerId() === null);

  /** Display text for the "Naam" value in view mode. */
  readonly generalLedgerName = computed(() => this.record()?.generalLedger_name ?? '');

  readonly startDateDisplay = computed(() =>
    formatDisplayDate(this.record()?.startDate, 'nl-NL'),
  );

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  /** Display text for the "Memo" value in view mode. */
  readonly memoDisplay = computed(() => this.record()?.memo_Value || 'n.v.t.');

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.generalLedgerId.set(id);
      this.loadGeneralLedger(id);
    } else {
      // Add mode: start directly in edit state.
      this.detailsEditing.set(true);
    }
  }

  private loadGeneralLedger(id: string): void {
    this.isLoading.set(true);
    this.generalLedgerService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ledger) => {
          if (!ledger) {
            this.notFound.set(true);
          } else {
            this.record.set(ledger);
            this.patchForm(ledger);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private patchForm(ledger: GeneralLedger): void {
    this.form.patchValue({
      generalLedgerName: ledger.generalLedger_name,
      startDate: toFormDate(ledger.startDate),
      endDate: this.isMaxEndDate(ledger.endDate) ? null : toFormDate(ledger.endDate),
      memoValue: ledger.memo_Value ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      generalLedgerName: string;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    const payload: GeneralLedger = {
      generalLedgerId: this.generalLedgerId() ?? '',
      generalLedger_name: value.generalLedgerName,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memo_Value: value.memoValue ?? '',
    };

    this.isSaving.set(true);

    const id = this.generalLedgerId();
    const save$ = id
      ? this.generalLedgerService.updateGeneralLedger(id, payload)
      : this.generalLedgerService.addGeneralLedger(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
        } else if (saved?.generalLedgerId) {
          this.router.navigate(['/config/generalledger/editable', saved.generalLedgerId]);
        } else {
          this.router.navigate(['/config/generalledger']);
        }
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  // --- Page actions -------------------------------------------------------------

  goBack(): void {
    this.router.navigateByUrl('config/generalledger');
  }

  goToGeneralLedgerAccount(): void {
    this.router.navigate(['/config/generalledgeraccount', this.generalLedgerId()]);
  }

  goToGeneralLedgerGroup(): void {
    this.router.navigateByUrl('config/generalledgergroup');
  }

  onDelete(): void {
    const id = this.generalLedgerId();
    if (!id) {
      return;
    }

    deleteGeneralLedgerWithChecks({
      id,
      service: this.generalLedgerService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.goBack(),
    });
  }
}
